import { useState, FormEvent, ChangeEvent } from 'react';
import { useLocation } from '../common/location/useLocation';
import { SchoolSearch } from '../common/SchoolSearch';
import { SearchResult } from './SearchResult';
import { PostSuccess } from './PostSuccess';
import { WordCountTextarea } from '../common/WordCountTextarea';
import { type GeoLocationPlace } from '../common/location/types';

const nonNull = (a: number | null) => a !== null;
const nonEmpty = (...args: string[]): boolean => {
    return args.every((a) => a != null && a.length > 0);
}
const validEmails = (...args: string[]): boolean => {
    return args.every(a => window.isEmailAddress(a));
}

export const IHeartRadioForm = () => {
    const [location] = useLocation();
    const [locationOverride, setLocationOverride] = useState<GeoLocationPlace | undefined>(undefined);

    const [selectedSchoolId, setSelectedSchoolId] = useState<number|null>(null);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [teacherName, setTeacherName] = useState("");
    const [teacherEmail, setTeacherEmail] = useState("");
    const [nominationReason, setNominationReason] = useState("");
    const [stationName, setStationName] = useState("");

    const [successfullySubmitted, setSuccessfullySubmitted] = useState(false);
    const [submissionError, setSubmissionError] = useState(false);

    const allowSubmit = nonNull(selectedSchoolId) && nonEmpty(name, teacherName, nominationReason, stationName) &&
            validEmails(email, teacherEmail);

    const submit = (e: FormEvent) => {
        e.preventDefault();
        const data = JSON.stringify({
            selectedSchoolId,
            name,
            email,
            teacherName,
            teacherEmail,
            nominationReason,
            stationName,
        });
        $.ajax({
            url: '/common/iheartradio_landing.html',
            data,
            dataType: 'json',
            // Spring seems to expect this for @RequestBody requests. Note that with this set, we have to pre-serialize
            // `data`.
            contentType: 'application/json',
            method: 'POST',
        }).done(() => {
            setSuccessfullySubmitted(true);
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }).fail(() => {
            setSubmissionError(true);
        });
    }
    
    if (successfullySubmitted) {
        // We shouldn't need this unless geolocation fails. But if geolocation fails, show them the area around the office.
        const fallbackLocation = {
            latLng: {
                lat: 40.7589,
                lng: -73.9913,
            },
            // PostSuccess can handle an undefined cityName. Let it.
            cityName: undefined,
        };
        const presentLocation = locationOverride || location || fallbackLocation;
        return <PostSuccess locationName={presentLocation.cityName} lat={presentLocation.latLng.lat} lng={presentLocation.latLng.lng} />
    }

    return (
            <div className="iheart">
                <div className="header">
                    <h1>Thank a teacher by giving them a chance to win $5,000 in classroom&nbsp;supplies</h1>
                    <p className="subheader">
                        This back-to-school season, iHeartRadio and DonorsChoose are teaming up to celebrate amazing public school teachers across the country – and we need your help to make it happen. Nominate your faves and show them the support they deserve!
                    </p>
                    <p className="subheader">
                        Select teachers will be featured on their local iHeartRadio station, and every nominated teacher is in the running for one of five $5,000 DonorsChoose gift cards for the supplies they need to make over their classroom.
                    </p>
                    <p className="subheader">
                        Click <a target="_blank" href="https://help.donorschoose.org/hc/en-us/articles/33060587158039-iHeart-Radio-Thank-a-Teacher-Promotion-FAQ">here</a> for the contest rules.
                    </p>
                    <p className="subheader">
                        Gift card winners will be announced
                        on the <a target="_blank" href="https://blog.donorschoose.org/">DonorsChoose blog</a>.
                    </p>
                </div>
                {submissionError && <div className='error-text'>Something went wrong! Please reload the page and try again.</div> }
                <form className="form-redux" onSubmit={submit}>
                    <div className="two-in-one">
                        <div className="input-holder">
                            <label htmlFor="name">Your name</label>
                            <input name="name" autoComplete="name" id="name" value={name} type="text" onChange={(e) => setName(e.currentTarget.value)}/>
                        </div>

                        <div className="input-holder">
                            <label htmlFor="email">Your email</label>
                            <input type="email" autoComplete="email" name="email" id="email" value={email} onChange={(e) => setEmail(e.currentTarget.value)}/>
                        </div>
                    </div>

                    <div className="two-in-one">
                        <div className="input-holder">
                            <label htmlFor="teacherName">Name of teacher you’re nominating</label>
                            <input name="teacherName" id="teacherName" type="text" value={teacherName} onChange={(e) => setTeacherName(e.currentTarget.value)}/>
                        </div>

                        <div className="input-holder">
                            <label htmlFor="teacherEmail">Teacher's email</label>
                            <input type="email" name="teacherEmail" id="teacherEmail" value={teacherEmail} onChange={(e) => setTeacherEmail(e.currentTarget.value)}/>
                        </div>
                    </div>

                    <div className="school-search-box">
                        <SchoolSearch
                                setSelectedSchool={setSelectedSchoolId}
                                clearSelectedSchool={() => {
                                    setSelectedSchoolId(null);
                                }}
                                location={locationOverride ?? location}
                                setLocation={setLocationOverride}
                                customSchoolSearchDetailsComponent={SearchResult}
                                skipAutofocus={true}
                                blockNewRequestForm={true}
                        />
                    </div>

                    <input type="hidden" value={selectedSchoolId ?? ""} name="schoolId" />

                    <div className="input-holder">
                        <label htmlFor="nominationReason">Tell us about this teacher and the impact they’re having on their students and community! (30-100 words)</label>
                        <WordCountTextarea
                                min={30}
                                max={100}
                                name="nominationReason"
                                id="nominationReason"
                                value={nominationReason}
                                onChange={(e) => setNominationReason((e.currentTarget as HTMLTextAreaElement).value)}
                                label=""
                        />
                    </div>
                    <div className="input-holder">
                        <label htmlFor="stationName">We&rsquo;re curious! Which iHeartRadio station do you listen to?</label>
                        <input type="text" maxLength={50} name="stationName" id="stationName" value={stationName} onChange={(e) => setStationName(e.currentTarget.value)}/>
                    </div>

                    <div className="disclaimer">
                        <p>By submitting this form, you give iHeartMedia and DonorsChoose permission to:</p>
                        <ul>
                            <li>Use the information in this form to feature on-air and on various online platforms owned by iHeartMedia and DonorsChoose, in the case that your teacher is selected for an on-air shout-out or to win one of five $5,000 DonorsChoose gift cards.</li>
                            <li>Store the information in this form for the purpose of this nomination campaign.</li>
                            <li>Send you emails related to this nomination campaign and other marketing. You may opt out of these communications at any time by contacting DonorsChoose or IHeartMedia directly.</li>
                        </ul>
                    </div>
                    <input className="button-primary full-width" type="submit" disabled={!allowSubmit} value="Submit" onClick={submit} />
                </form>
            </div>
    );
};
